import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlane,
  FaHotel,
  FaGlobe,
  FaCar,
  FaBell,
  FaUser,
  FaBars,
  FaChevronDown,
  FaEllipsisH,
  FaMapMarkedAlt,
} from "react-icons/fa";
import logo from "../assets/logo.png";
import { useSelector, useDispatch } from "react-redux";
import {
  checkUserLogout,
  checkUserLogin,
} from "../redux/reduxThunk/thunkAuthUser/AuthUser";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";

const Navbar = ({ activeTab, setActiveTab }) => {
  const { user } = useAuth();
  const { currency, changeCurrency } = useCurrency();
  const location = useLocation(); // useLocation hook जोड़ें

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [checkLogin, setCheckLogin] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authdata = useSelector((state) => state.auth.authdata);
  const isLoggedIn = Boolean(authdata?.token);

  // URL के आधार पर activeTab सेट करें
  useEffect(() => {
    const path = location.pathname;
    if (path === "/" || path.includes("/flight")) {
      setActiveTab("flight");
    } else if (path.includes("/hotel")) {
      setActiveTab("hotel");
    } else if (path.includes("/visa")) {
      setActiveTab("visa");
    } else if (path.includes("/activity")) {
      setActiveTab("activity");
    }
  }, [location, setActiveTab]);

  useEffect(() => {
    dispatch(checkUserLogin())
      .then((res) => {
        if (res?.payload?.success) {
          setCheckLogin(true);
        } else {
          setCheckLogin(false);
        }
      })
      .catch((err) => {
        console.error(err, "error while check user login");
      });
  }, [dispatch, isLoggedIn, user]);

  const serviceTabs = [
    { id: "flight", name: "Flight", icon: FaPlane },
    { id: "hotel", name: "Hotel", icon: FaHotel },
    { id: "visa", name: "Visa", icon: FaGlobe },
    { id: "activity", name: "Activity", icon: FaMapMarkedAlt },
  ];

  const handleServiceClick = (id) => {
    setActiveTab(id); // Active tab update करें
    if (id === "flight") navigate("/");
    if (id === "hotel") navigate("/hotelpage");
    if (id === "visa") navigate("/visapage");
    if (id === "activity") navigate("/activity");
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handleOpenOption = () => {
    setShowUserMenu((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && event.target.closest(".user-menu-trigger") === null) {
        const menu = document.querySelector(".user-menu-dropdown");
        if (menu && !menu.contains(event.target)) setShowUserMenu(false);
      }
      if (showCurrencyMenu && event.target.closest(".currency-menu-trigger") === null) {
        const menu = document.querySelector(".currency-menu-dropdown");
        if (menu && !menu.contains(event.target)) setShowCurrencyMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu, showCurrencyMenu]);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Left Section */}
          <div className="flex items-center space-x-8">
            {/* Logo */}
            <motion.div
              className="flex"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <NavLink to="/">
                <img
                  src={logo}
                  alt="Logo"
                  className="h-30 overflow-y-hidden lg:h-44 w-auto object-contain cursor-pointer"
                />
              </NavLink>
            </motion.div>

            {/* Desktop Menu (NO DROPDOWN NOW) */}
            <div className="hidden lg:flex items-center space-x-8">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `text-gray-700 hover:text-blue-600 font-medium ${
                    isActive ? "text-blue-600" : ""
                  }`
                }
              >
                Home
              </NavLink>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `text-gray-700 hover:text-blue-600 font-medium ${
                    isActive ? "text-blue-600" : ""
                  }`
                }
              >
                About Us
              </NavLink>

              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `text-gray-700 hover:text-blue-600 font-medium ${
                    isActive ? "text-blue-600" : ""
                  }`
                }
              >
                Contact
              </NavLink>

              <NavLink
                to="/blog"
                className={({ isActive }) =>
                  `text-gray-700 hover:text-blue-600 font-medium ${
                    isActive ? "text-blue-600" : ""
                  }`
                }
              >
                Blog
              </NavLink>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Service Tabs */}
            <div className="hidden sm:flex items-center space-x-4 bg-gray-100 rounded-lg p-1">
              {serviceTabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => handleServiceClick(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:bg-white hover:text-gray-800"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="text-lg" />
                  <span className="font-medium">{tab.name}</span>
                </motion.button>
              ))}
            </div>

            {/* Currency Selector */}
            <div className="relative currency-menu-trigger">
              <motion.button
                onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{currency === 'INR' ? '₹' : currency === 'USD' ? '$' : '€'}</span>
                <span className="hidden sm:inline">{currency}</span>
                <FaChevronDown className="text-xs" />
              </motion.button>

              <AnimatePresence>
                {showCurrencyMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 currency-menu-dropdown"
                  >
                    {['INR', 'USD', 'EUR'].map((curr) => (
                      <button
                        key={curr}
                        onClick={() => {
                          changeCurrency(curr);
                          setShowCurrencyMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          currency === curr
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mr-2">{curr === 'INR' ? '₹' : curr === 'USD' ? '$' : '€'}</span>
                        {curr}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Icons */}
            {/* <motion.button
              className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
            >
              <FaBell className="text-lg sm:text-xl" />
              <span className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></span>
            </motion.button> */}

            <motion.div
              className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center cursor-pointer user-menu-trigger"
              whileHover={{ scale: 1.05 }}
              onClick={handleOpenOption}
            >
              <FaUser className="text-gray-600 text-sm sm:text-lg" />
            </motion.div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-blue-600"
            >
              <motion.div
                animate={{ rotate: isMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaBars className="text-lg sm:text-xl" />
              </motion.div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden border-t border-gray-200"
            >
              <div className="py-4 space-y-4">
                {/* Mobile Service Tabs */}
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Services
                  </h3>
                  <div className="space-y-2">
                    {serviceTabs.map((tab) => (
                      <motion.button
                        key={tab.id}
                        onClick={() => {
                          handleServiceClick(tab.id); // handleServiceClick use करें
                          setIsMenuOpen(false);
                        }}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          activeTab === tab.id
                            ? "bg-blue-600 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <tab.icon className="text-lg" />
                        <span className="font-medium">{tab.name}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Mobile Menu Items */}
                <div className="space-y-2">
                  <NavLink
                    to="/"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
                  >
                    Home
                  </NavLink>
                  <NavLink
                    to="/about"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
                  >
                    About Us
                  </NavLink>

                  <NavLink
                    to="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
                  >
                    Contact
                  </NavLink>

                  <NavLink
                    to="/blog"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-gray-700 hover:text-blue-600 font-medium"
                  >
                    Blog
                  </NavLink>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* User Menu */}
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-20 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 user-menu-dropdown"
          >
            <NavLink
              to="/bookings"
              onClick={() => setShowUserMenu(false)}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
            >
              Booking Details
            </NavLink>

            {checkLogin && (
              <NavLink
                to="/profile"
                onClick={() => setShowUserMenu(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              >
                Profile
              </NavLink>
            )}

            {authdata?.token?.role === "admin" && (
              <NavLink
                to="/admin/booking"
                onClick={() => setShowUserMenu(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              >
                Admin Dashboard
              </NavLink>
            )}

            {checkLogin ? (
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  dispatch(checkUserLogout())
                    .unwrap()
                    .then((res) => {
                      if (!res?.success) {
                        window.alert("user did not logout");
                        return;
                      }
                      window.alert("User logout successful");

                      setCheckLogin(false);
                      navigate("/");
                    })
                    .catch((error) => {
                      console.error("Logout failed:", error);
                      window.alert("user logout failed");
                    });
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600"
              >
                Logout
              </button>
            ) : (
              <NavLink
                to="/login"
                onClick={() => setShowUserMenu(false)}
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              >
                Login
              </NavLink>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
